import type { Difficulty, MCQQuestion } from './types';

function mulberry32(seed: number): () => number {
  let state = seed | 0;
  return function () {
    state = (state + 0x6d2b79f5) | 0;
    let t = state;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function randInt(rand: () => number, min: number, max: number): number {
  return Math.floor(rand() * (max - min + 1)) + min;
}

function fmt1(n: number): string {
  return (Math.round(n * 10) / 10).toFixed(1);
}

type QType = 'dec_to_frac' | 'frac_to_dec' | 'place_value' | 'number_line';

/**
 * N-3-10: one-decimal-place concept and tenth-place value.
 * difficulty 1: 0.x <-> x/10 conversion
 * difficulty 2: digit identification (individual vs. tenth place)
 * difficulty 3: number line positioning
 */
export function generateDecimalBasic(seed: number, difficulty: Difficulty): MCQQuestion {
  const rand = mulberry32(seed);

  const qTypes: QType[] = difficulty === 1
    ? ['dec_to_frac', 'frac_to_dec']
    : difficulty === 2
    ? ['dec_to_frac', 'frac_to_dec', 'place_value']
    : ['place_value', 'number_line', 'dec_to_frac'];

  const qType = qTypes[randInt(rand, 0, qTypes.length - 1)]!;

  let prompt: string;
  let correct: string;
  let options: string[];
  let explanation: string;

  if (qType === 'dec_to_frac') {
    const num = randInt(rand, 1, 9);
    const dec = num / 10;
    correct = num + '/10';
    prompt = fmt1(dec) + ' 用分數表示是？';
    const w1 = (num + 1) + '/10';
    const w2 = num + '/100';
    const w3 = '1/' + num;
    options = [correct, w1, w2, w3];
    explanation = fmt1(dec) + ' 是十分之' + num + '，寫成分數 ' + correct + '。';

  } else if (qType === 'frac_to_dec') {
    const num = randInt(rand, 1, 9);
    const dec = num / 10;
    correct = fmt1(dec);
    prompt = num + '/10（十分之' + num + '）用小數表示是？';
    const w1 = fmt1((num + 1) / 10);
    const w2 = fmt1(num);
    const w3 = fmt1((num - 1 > 0 ? num - 1 : num + 2) / 10);
    options = [correct, w1, w2, w3];
    explanation = '十分之' + num + ' = 0.' + num + ' → ' + correct + '。小數點右邊第一位是十分位。';

  } else if (qType === 'place_value') {
    const wholeD = difficulty === 2 ? randInt(rand, 0, 9) : randInt(rand, 1, 9);
    const tenthD = randInt(rand, 0, 9);
    const num = wholeD + tenthD / 10;
    const askTenth = rand() > 0.5;
    const place = askTenth ? '十分位' : '個位';
    const digit = askTenth ? tenthD : wholeD;
    prompt = fmt1(num) + ' 的' + place + '數字是幾？';
    correct = String(digit);
    const wrongs = [0,1,2,3,4,5,6,7,8,9]
      .filter(d => d !== digit)
      .sort(() => rand() - 0.5)
      .slice(0, 3)
      .map(String);
    options = [correct, ...wrongs];
    explanation = fmt1(num) + ' 的' + place + '是 ' + digit + '。';

  } else {
    const lo = randInt(rand, 0, 8);
    const hi = lo + 1;
    const mid = lo + 0.5;
    correct = fmt1(mid);
    prompt = lo + ' 和 ' + hi + ' 之間是哪個小數？';
    const w1 = fmt1(hi + 0.5);
    const w2 = fmt1(lo - 0.5 >= 0 ? lo - 0.5 : lo + 1.5);
    const w3 = fmt1(hi + 1);
    options = [correct, w1, w2, w3];
    explanation = lo + ' 到 ' + hi + ' 中間是 ' + correct + '，因為 ' + lo + ' < ' + correct + ' < ' + hi + '。';
  }

  const seen = new Set();
  const uniqueOpts: string[] = [];
  for (const o of options) {
    if (!seen.has(o)) { seen.add(o); uniqueOpts.push(o); }
  }
  while (uniqueOpts.length < 4) {
    const extra = fmt1(randInt(rand, 1, 99) / 10);
    if (!seen.has(extra) && extra !== correct) { seen.add(extra); uniqueOpts.push(extra); }
  }
  if (!uniqueOpts.includes(correct)) uniqueOpts[0] = correct;

  const shuffled = uniqueOpts.slice(0, 4).sort(() => rand() - 0.5);

  return {
    kind: 'mcq',
    prompt,
    options: shuffled,
    correctAnswer: shuffled.indexOf(correct),
    explanation,
    kp: 'math.decimal.basic',
  };
}
